"use client"

import React, { useState, useEffect, ReactNode } from 'react'
import { Group, Text, ActionIcon, Button, Menu, Skeleton } from '@mantine/core'
import { FiGlobe, FiChevronDown } from 'react-icons/fi'

export interface Locale {
  value: string
  label: string
  flag?: ReactNode | string
  countryCode?: string
}

export interface LanguageSelectorProps {
  locales: Locale[]
  currentLocale: string
  onLocaleChange: (locale: string) => void
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'filled' | 'outline' | 'light' | 'white' | 'default' | 'subtle' | 'transparent'
  placeholder?: string
  label?: string
  showIcon?: boolean
  disabled?: boolean
  clearable?: boolean
  displayVariant?: 'full' | 'compact' | 'icon'
}

export function LanguageSelector({
  locales,
  currentLocale,
  onLocaleChange,
  size = 'sm',
  variant = 'filled',
  placeholder,
  label,
  showIcon = true,
  disabled = false,
  displayVariant = 'full'
}: LanguageSelectorProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Prevent hydration mismatch
  if (!mounted) {
    if (displayVariant === 'icon') {
      return (
        <ActionIcon
          size={size}
          variant={variant}
          disabled
        >
          <FiGlobe size="1rem" />
        </ActionIcon>
      )
    }

    const skeletonHeight = { xs: 30, sm: 36, md: 42, lg: 50, xl: 60 }[size]
    return <Skeleton height={skeletonHeight} radius="sm" width={displayVariant === 'compact' ? 80 : '100%'} />
  }

  const renderFlag = (locale: Locale) => {
    if (!locale.flag) return null
    if (typeof locale.flag !== 'string') return locale.flag
    return <Text size="sm">{locale.flag}</Text>
  }

  const currentLocaleData = locales.find(l => l.value === currentLocale)
  const displayLabel = displayVariant === 'compact'
    ? currentLocale.toUpperCase()
    : currentLocaleData?.label ?? placeholder ?? currentLocale

  const menuItems = locales.map((locale) => (
    <Menu.Item
      key={locale.value}
      onClick={() => onLocaleChange(locale.value)}
      fw={currentLocale === locale.value ? 600 : 400}
      bg={currentLocale === locale.value ? 'blue.0' : undefined}
    >
      <Group gap="xs">
        {renderFlag(locale)}
        <Text size="sm">
          {displayVariant === 'compact' ? locale.value.toUpperCase() : locale.label}
        </Text>
      </Group>
    </Menu.Item>
  ))

  if (displayVariant === 'icon') {
    return (
      <Menu shadow="md" width={200} position="bottom-end">
        <Menu.Target>
          <ActionIcon
            size={size}
            variant={variant}
            disabled={disabled}
          >
            <FiGlobe size="1rem" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {label && <Menu.Label>{label}</Menu.Label>}
          {menuItems}
        </Menu.Dropdown>
      </Menu>
    )
  }

  return (
    <Menu shadow="md" width={displayVariant === 'compact' ? 120 : 200} position="bottom-start">
      <Menu.Target>
        <Button
          size={size}
          variant={variant === "transparent" ? "subtle" : variant}
          disabled={disabled}
          leftSection={showIcon ? <FiGlobe size="1rem" /> : undefined}
          rightSection={<FiChevronDown size="0.8rem" />}
          className={displayVariant === 'compact' ? 'w-auto min-w-15' : undefined}
        >
          {displayLabel}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {label && <Menu.Label>{label}</Menu.Label>}
        {menuItems}
      </Menu.Dropdown>
    </Menu>
  )
}
